import { Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useKnowledgeTree } from '@/lib/api/knowledge-base';

import { StatusBadge, VisibilityBadge } from './kb-badges';
import { KbTree } from './kb-tree';

export function KbBrowsePage() {
  const treeQuery = useKnowledgeTree();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const tree = treeQuery.data;
  const folders = tree?.folders ?? [];
  const articles = tree?.articles ?? [];

  const articleCountByFolder = new Map<string, number>();
  for (const article of articles) {
    articleCountByFolder.set(
      article.folderId,
      (articleCountByFolder.get(article.folderId) ?? 0) + 1,
    );
  }

  // Default to the first folder (by path) once the tree loads.
  const sortedFolders = [...folders].toSorted((a, b) => a.path.localeCompare(b.path));
  const effectiveSelectedId =
    selectedId && folders.some((folder) => folder.id === selectedId)
      ? selectedId
      : (sortedFolders[0]?.id ?? null);
  const selectedFolder = folders.find((folder) => folder.id === effectiveSelectedId);
  const folderArticles = articles
    .filter((article) => article.folderId === effectiveSelectedId)
    .toSorted((a, b) => a.title.localeCompare(b.title));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Knowledge Base</h1>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Author, approve, and archive the K-beauty articles the agents read.
        </p>
      </div>

      {treeQuery.isPending && <p className="text-sm">Loading…</p>}
      {treeQuery.isError && (
        <p className="text-sm text-[var(--color-destructive)]">
          Failed to load the knowledge base: {treeQuery.error.message}
        </p>
      )}

      {tree && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,20rem)_1fr]">
          <Card>
            <CardContent className="pt-6">
              <KbTree
                folders={folders}
                articleCountByFolder={articleCountByFolder}
                selectedId={effectiveSelectedId}
                onSelect={(id) => setSelectedId(id === '' ? null : id)}
              />
            </CardContent>
          </Card>

          <div className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold">
                  {selectedFolder ? selectedFolder.path : 'No folder selected'}
                </h2>
                <p className="text-sm text-[var(--color-muted-foreground)]">
                  {selectedFolder
                    ? `${folderArticles.length} article(s)`
                    : 'Select or create a folder to add articles.'}
                </p>
              </div>
              {selectedFolder && (
                <Button asChild>
                  <Link to="/knowledge-base/articles/new" search={{ folderId: selectedFolder.id }}>
                    <Plus className="size-4" />
                    New article
                  </Link>
                </Button>
              )}
            </div>

            {selectedFolder && (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Visibility</TableHead>
                      <TableHead>Owner</TableHead>
                      <TableHead>Updated</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {folderArticles.length ? (
                      folderArticles.map((article) => (
                        <TableRow key={article.id}>
                          <TableCell>
                            <Link
                              to="/knowledge-base/articles/$articleId/edit"
                              params={{ articleId: article.id }}
                              className="font-medium hover:underline"
                            >
                              {article.title}
                            </Link>
                            <span className="block text-xs text-[var(--color-muted-foreground)]">
                              {article.slug}
                            </span>
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={article.status} />
                          </TableCell>
                          <TableCell>
                            <VisibilityBadge visibility={article.visibility} />
                          </TableCell>
                          <TableCell className="text-sm">{article.ownerEmail}</TableCell>
                          <TableCell className="text-sm">
                            {new Date(article.updatedAt).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} className="h-24 text-center">
                          No articles in this folder yet.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
